import { useQuery } from "@tanstack/react-query";
import { stormImpactModelsApi } from "@/api";
import type { StormImpactModelDetailed } from "@/api/types";
import type { AvailableRun, AvailableRunsParams } from "@/api/types";

/**
 * Fetch storm impact models
 * Returns the items array from the API response
 */
export function useStormImpactModels(
  applicationId?: string | null,
  options: { useFallback?: boolean } = {},
) {
  const { useFallback = true } = options;
  const effectiveAppId =
    applicationId ??
    (useFallback
      ? (localStorage.getItem("current_application_id") ?? undefined)
      : undefined);

  const { data, isLoading, error } = useQuery({
    queryKey: ["models", "storm", effectiveAppId],
    queryFn: async () => {
      const response = await stormImpactModelsApi.stormImpactModels.list(
        effectiveAppId
      );
      return response.items as StormImpactModelDetailed[];
    },
    enabled: !!effectiveAppId,
    staleTime: Infinity,
  });

  return { data, loading: isLoading, error };
}

/**
 * Fetch available model runs for a given date and model combination
 *
 * @param params - The parameters for fetching available runs
 * @param params.stormImpactModelId - UUID of the selected storm impact model
 * @param params.weatherModelId - UUID of the selected weather model
 * @param params.forecastDate - ISO date string (e.g., "2025-01-08")
 *
 * Only fetches when all params are provided (skip if any is missing)
 */
export function useAvailableRuns(params: Partial<AvailableRunsParams>) {
  const { stormImpactModelId, weatherModelId, forecastDate, isExport } = params;

  const isEnabled = !!stormImpactModelId && !!weatherModelId && !!forecastDate;

  const { data, isLoading, error } = useQuery({
    queryKey: ["runs", "available", { stormImpactModelId, weatherModelId, forecastDate, isExport }],
    queryFn: () =>
      stormImpactModelsApi.availableRuns.list({
        stormImpactModelId: stormImpactModelId!,
        weatherModelId: weatherModelId!,
        forecastDate: forecastDate!,
        isExport,
      }),
    enabled: isEnabled,
  });

  return { 
    data: data || [], 
    loading: isLoading, 
    error 
  };
}

/**
 * Fetch latest available runs using the precomputed max_forecast_date.
 *
 * The weather model object includes max_forecast_date from the backend,
 * so we pass it as forecastDate directly. This avoids a slow backend
 * lookup across all forecast partitions.
 *
 * @param params.stormImpactModelId - UUID of the selected storm impact model
 * @param params.weatherModelId - UUID of the selected weather model
 * @param params.maxForecastDate - Precomputed max date from the weather model
 *
 * Only fetches when model IDs and maxForecastDate are provided
 */
export function useLatestAvailableRuns(params: {
  stormImpactModelId?: string;
  weatherModelId?: string;
  maxForecastDate?: string;
}) {
  const { stormImpactModelId, weatherModelId, maxForecastDate } = params;

  const isEnabled = !!stormImpactModelId && !!weatherModelId && !!maxForecastDate;

  const { data, isLoading, error } = useQuery({
    queryKey: ["runs", "latest", { stormImpactModelId, weatherModelId, maxForecastDate }],
    queryFn: () =>
      stormImpactModelsApi.availableRuns.list({
        stormImpactModelId: stormImpactModelId!,
        weatherModelId: weatherModelId!,
        forecastDate: maxForecastDate!,
      }),
    enabled: isEnabled,
  });

  return { 
    data: data || [], 
    loading: isLoading, 
    error 
  };
}
